import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Backdrop } from "@mui/material";

type OnClickEventChain = Record<string, () => void>;

export interface BackdropApi {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onClickCallback: { current: OnClickEventChain };
  lastKey: { current: string };
}

const BackDropContext = createContext<BackdropApi>(null);

export const BackdropContextProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const onClickCallback = useRef<OnClickEventChain>({});
  const lastKey = useRef("");

  const onClickHandler = useCallback(() => {
    setIsOpen(false);
    if (
      onClickCallback.current &&
      Object.entries(onClickCallback.current).length
    ) {
      Object.values(onClickCallback.current).forEach((cb) => cb?.());
    }
  }, []);

  const backDropApi = useMemo(
    () => ({ isOpen, setIsOpen, onClickCallback, lastKey }),
    [isOpen],
  );

  return (
    <BackDropContext.Provider value={backDropApi}>
      <Backdrop
        open={isOpen}
        onClick={onClickHandler}
        sx={{
          zIndex: (theme) =>
            !isOpen ? -1 : Math.max(theme.zIndex.drawer + 1, 1000),
        }}
      />
      {children}
    </BackDropContext.Provider>
  );
};

export const usePatientBackdrop = (key: string, onClose: () => void) => {
  const { isOpen, setIsOpen, onClickCallback, lastKey } = React.useContext(
    BackDropContext,
  );

  useEffect(() => {
    if (onClose && onClickCallback.current)
      onClickCallback.current[key] = onClose;
  }, [key, onClose, onClickCallback]);

  const showBackdrop = useCallback(
    (show, forKey = "") => {
      if (!show && lastKey.current !== forKey) {
        // If we are hiding and the key is not the last key, we don't want to hide
        return;
      }
      // If we're showing, keep last key
      if (show && lastKey.current !== forKey) lastKey.current = forKey;

      if (show) {
        console.log("Show backdrop");
        setIsOpen(true);
      } else {
        console.log("hide backdrop");
        lastKey.current = "";
        delete onClickCallback.current[forKey];
        setIsOpen(false);
      }
    },
    [onClickCallback, setIsOpen, lastKey],
  );

  return { isOpen, showBackdrop };
};
